import React, { useCallback, useState, useRef, useEffect } from 'react';
import { AppModal } from '@/components/compose/app.modal';
import { Box } from '@/components/base/box';
import { AppButton } from '@/components/base/app.button';
import styled from 'styled-components/native';
import { useUser } from '@/hooks/use.user';
import { AvailabilityBlock, sortAvailabilityBlocks, translateDay } from '@matap/shared';
import { AppListView } from '@/components/base/app.list.view';
import { ListRenderItemInfo, TextInput, TextInputProps, Pressable } from 'react-native';
import { AppTextView } from '@/components/base/app.text.view';
import { AppIconButton } from '@/components/base/app.icon.button';
import { useAppDispatch } from '@/hooks/use.redux';
import { actionSetUser } from '@/redux';
import { usePatchProfile } from '@/apis/user';
import { hpVal, themeVal, wpVal } from '../../utils';
import { AppCardView } from '@/components/base/app.card.view';
import { useAvailabilitiesHook } from '@matap/shared/dist/src/react/use.availabilities.hook';

export default function AvailabilityModal() {
  const { user } = useUser();
  const dispatch = useAppDispatch();
  const { set, deleteBlock, updateBlock, blocksByDay, addBlock, savable } = useAvailabilitiesHook(user.doctorProfile.availability);

  const { mutate: updateProfile, isPending } = usePatchProfile({
    onSuccess: (data) => {
      dispatch(actionSetUser(data));
      set(data.doctorProfile.availability);
    },
  });

  const save = useCallback(() => {
    updateProfile({ body: { name: user.name, gender: user.gender, availability: savable() } });
  }, [savable, user, updateProfile]);

  const daysArray = useRef(['6', '0', '1', '2', '3', '4', '5']).current;

  const renderDayItem = useCallback(
    ({ item: day }: ListRenderItemInfo<string>) => {
      const dayNum = Number(day);
      const dayBlocks = blocksByDay[dayNum] || [];
      return (
        <DayCard fullWidth shadowColor={'#000'} shadowOffsetX={1} shadowOffsetY={1} shadowOpacity={0.1} blurRadius={6}>
          <DayHeader>
            <DayTitleRow>
              <DayAccentBar />
              <DayName>{translateDay(dayNum)}</DayName>
            </DayTitleRow>

            <AddPillButton onPress={() => addBlock(dayNum)}>
              <AppTextView variant={'small'} bold textColor={'jade_dark'} text={'+ افزودن زمان'} />
            </AddPillButton>
          </DayHeader>

          {dayBlocks.length === 0 ? (
            <EmptyHint>
              <AppTextView variant={'small'} textColor={'grey_light'} text={'هیچ بازه فعالی برای این روز تنظیم نشده است'} />
            </EmptyHint>
          ) : (
            dayBlocks.map((block, idx) => (
              <Row key={`${day}-${idx}`} block={block} onRemove={() => deleteBlock(block, dayNum)} onChange={(b) => updateBlock(b, dayNum, idx)} />
            ))
          )}
        </DayCard>
      );
    },
    [blocksByDay, addBlock, deleteBlock, updateBlock],
  );

  return (
    <AppModal title={'بازه‌های زمانی فعالیت'}>
      <Container flex={1}>
        <List data={daysArray} renderItem={renderDayItem} keyExtractor={(item) => item} contentContainerStyle={{ paddingBottom: 40 }} />
        <SaveContainer fullWidth>
          <SaveButton title={'ذخیره تغییرات'} loading={isPending} fullWidth onPress={save} />
        </SaveContainer>
      </Container>
    </AppModal>
  );
}

const Row = React.memo(function Row({ block, onRemove, onChange }: { block: AvailabilityBlock; onRemove: () => void; onChange: (block: AvailabilityBlock) => void }) {
  const [sh, sm] = (block.startTime || ':').split(':');
  const [eh, em] = (block.endTime || ':').split(':');

  const [startTimeHour, setStartTimeHour] = useState(sh || '');
  const [startTimeMin, setStartTimeMin] = useState(sm || '');
  const [endTimeHour, setEndTimeHour] = useState(eh || '');
  const [endTimeMin, setEndTimeMin] = useState(em || '');

  const onChangeRef = useRef(onChange);
  const blockRef = useRef(block);

  useEffect(() => {
    onChangeRef.current = onChange;
    blockRef.current = block;
  }, [onChange, block]);

  const handleTimeChange = useCallback(
    (type: 'sh' | 'sm' | 'eh' | 'em', value: string) => {
      let finalSH = startTimeHour,
        finalSM = startTimeMin,
        finalEH = endTimeHour,
        finalEM = endTimeMin;

      if (type === 'sh') {
        setStartTimeHour(value);
        finalSH = value;
      }
      if (type === 'sm') {
        setStartTimeMin(value);
        finalSM = value;
      }
      if (type === 'eh') {
        setEndTimeHour(value);
        finalEH = value;
      }
      if (type === 'em') {
        setEndTimeMin(value);
        finalEM = value;
      }

      onChangeRef.current({
        ...blockRef.current,
        startTime: `${finalSH}:${finalSM}`,
        endTime: `${finalEH}:${finalEM}`,
      });
    },
    [startTimeHour, startTimeMin, endTimeHour, endTimeMin],
  );

  return (
    <RowContainer>
      <RemoveWrapper>
        <AppIconButton icon={'close'} color={'#ef4444'} size={'small'} onPress={onRemove} />
      </RemoveWrapper>

      <TimeBlock>
        <LabelText text={'از'} />
        <TimePill>
          <TimeInput value={startTimeHour} placeholder={'س'} onChangeText={(text) => handleTimeChange('sh', text)} />
          <DividerColon text={':'} />
          <TimeInput value={startTimeMin} placeholder={'د'} onChangeText={(text) => handleTimeChange('sm', text)} />
        </TimePill>

        <LabelText text={'تا'} />
        <TimePill>
          <TimeInput value={endTimeHour} placeholder={'س'} onChangeText={(text) => handleTimeChange('eh', text)} />
          <DividerColon text={':'} />
          <TimeInput value={endTimeMin} placeholder={'د'} onChangeText={(text) => handleTimeChange('em', text)} />
        </TimePill>
      </TimeBlock>
    </RowContainer>
  );
});

function TimeInput({ onChangeText, ...rest }: TextInputProps) {
  return (
    <TimeField
      {...rest}
      placeholderTextColor={'#94a3b8'}
      inputMode={'numeric'}
      maxLength={2}
      style={{
        textAlignVertical: 'center',
        paddingVertical: 0,
        justifyContent: 'center',
        alignItems: 'center',
      }}
      selectionColor={'#198b8c'}
      onChangeText={(text) => {
        if (text.length > 2) return;
        if (onChangeText) onChangeText(text);
      }}
    />
  );
}

const Container = styled(Box)`
  padding: ${hpVal(1)} ${wpVal(1)};
`;

const List = styled(AppListView)`
  width: 100%;
`;

const DayCard = styled(AppCardView)`
  background: white;
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 12px;
  border-width: 1px;
  border-color: #f1f5f9;
  width: 98%;
  align-self: center;
`;

const DayHeader = styled.View`
  flex-direction: row-reverse;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom-width: 1px;
  border-bottom-color: #f1f5f9;
`;

const DayTitleRow = styled.View`
  flex-direction: row-reverse;
  align-items: center;
  gap: 8px;
`;

const DayAccentBar = styled.View`
  width: 4px;
  height: 18px;
  background-color: #198b8c;
  border-radius: 2px;
`;

const DayName = styled(AppTextView).attrs({
  variant: 'medium',
  bold: true,
  textColor: 'blue',
})``;

const AddPillButton = styled(Pressable)`
  background-color: rgba(25, 139, 140, 0.08);
  padding-horizontal: 12px;
  padding-vertical: 6px;
  border-radius: 20px;
`;

const EmptyHint = styled(Box)`
  align-items: center;
  justify-content: center;
  padding-vertical: 20px;
  background-color: #f8fafc;
  border-radius: 10px;
  border-width: 1px;
  border-style: dashed;
  border-color: #cbd5e1;
`;

const RowContainer = styled.View`
  flex-direction: row-reverse;
  align-items: center;
  margin-top: 10px;
  background-color: #f8fafc;
  padding: 10px;
  border-radius: 12px;
  border-width: 1px;
  border-color: #f1f5f9;
`;

const RemoveWrapper = styled.View`
  background-color: #fee2e2;
  border-radius: 500px;
  padding: 2px;
  margin-left: 12px;
`;

const TimeBlock = styled.View`
  flex-direction: row-reverse;
  align-items: center;
  flex: 1;
  justify-content: flex-start;
  gap: 10px;
`;

const TimePill = styled.View`
  flex-direction: row;
  align-items: center;
  border-width: 1px;
  border-color: #cbd5e1;
  border-radius: 8px;
  background: white;
  padding-horizontal: 8px;
  height: 38px;
`;

const LabelText = styled(AppTextView).attrs({
  variant: 'small',
  bold: true,
  textColor: 'grey',
})`
  min-width: 16px;
  text-align: center;
`;

const DividerColon = styled(AppTextView).attrs({
  variant: 'small',
  bold: true,
  textColor: 'grey_light',
})`
  margin-horizontal: 2px;
`;

const TimeField = styled(TextInput)`
  width: 28px;
  height: 100%;
  text-align: center;
  font-size: ${themeVal((t) => t.fontSizes.text.primary)}px;
  color: #0f172a;
  font-family: ${({ theme }) => theme.fonts.primary};
`;

const SaveContainer = styled(Box)`
  padding-vertical: 16px;
  background-color: white;
  align-items: center;
`;

const SaveButton = styled(AppButton)`
  border-radius: 12px;
  height: 48px;
  width: 90%;
`;
